import React from 'react'
import PropTypes from 'prop-types'
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup'
import cssModules from 'react-css-modules'

import { getI18n } from 'client/i18n'
import { getStepName } from 'lib/route_utils'
import { hubPageUrl } from 'lib/campaign_url'

import StandardDialog from 'components/common/standard_dialog'
import ModalWizard from 'components/modal_wizard'
import Modal from 'components/common/modal'
import StandardError from 'components/common/standard_error'
import PreviewHeader from 'components/pages/campaign/preview_header'
import ModalAnimations from 'components/common/transitions/modal_animations'
import {
  GN_DISCLAIMERS,
  POOLS_CAMPAIGN_TYPES,
  SCREEN_NAME_MAPPING,
} from 'shared/shared_consts'
import {
  trackImpression,
  trackClick,
  trackEventNameClick,
} from 'lib/fpti_analytics'
import { Button } from '@paypalcorp/pp-react'

import styles from './create.less'

export const TRANSITION_DURATION = 750
export const CREATION_BLOCKS = {
  byKillSwitch: 'kill_switched',
  byCountry: 'blocked',
  byAccountType: 'account_type_blocked',
  byUnconfirmed: 'unconfirmed',
  byAccountRestricted: 'account_resrticted',
  byGNAccountRestricted: 'error_account_restricted_title',
}

const { CROWD_FUNDING } = POOLS_CAMPAIGN_TYPES
const i18n = getI18n('components/pages/create/create')

const ModalWithAnimations = props => {
  return (
    <ModalAnimations>
      <ModalWizard {...props} />
    </ModalAnimations>
  )
}

class CreatePage extends React.Component {
  static propTypes = {
    blockType: PropTypes.oneOf(Object.values(CREATION_BLOCKS)),
    campaignType: PropTypes.string,
    children: PropTypes.any,
    countryName: PropTypes.string,
    creationError: PropTypes.object,
    email: PropTypes.string,
    hideClose: PropTypes.bool,
    location: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    operatingRemotely: PropTypes.bool,
    countryCode: PropTypes.string,
    charity: PropTypes.object,
    cdnHost: PropTypes.string,
    params: PropTypes.object,
    routes: PropTypes.array.isRequired,
  }

  state = {
    showCloseDialog: false,
  }

  trackFPTI = (type, eventName, link) => {
    const currentStep =
      this.props.campaignType === CROWD_FUNDING
        ? getStepName('create-crowdfunding', this.props.routes)
        : getStepName('create', this.props.routes)
    let eccd
    if (
      this.props.blockType === CREATION_BLOCKS.byAccountRestricted ||
      this.props.blockType === CREATION_BLOCKS.byGNAccountRestricted
    ) {
      eccd = 'MATCH'
    } else {
      return false
    }

    if (type === 'impression') {
      trackImpression({
        event_name: eventName,
        screen: SCREEN_NAME_MAPPING[currentStep],
        eccd: eccd,
        error_desc: this.props.blockType,
      })
    } else {
      trackClick({
        event_name: eventName,
        eccd: eccd,
        link: link,
      })
    }
  }

  getFPTITags = () => {
    const currentStep =
      this.props.campaignType === CROWD_FUNDING
        ? getStepName('create-crowdfunding', this.props.routes)
        : getStepName('create', this.props.routes)
    const { charity } = this.props
    return {
      charity_name: charity && charity.name,
      npid: charity && charity.nonprofit_id,
      flow:
        charity || currentStep === 'nonprofit'
          ? 'GENEROSITY_NETWORK_CHARITIES'
          : 'GENEROSITY_NETWORK',
    }
  }

  handleDismissEvents = currentStep => {
    let stepToEventNameMapping = {
      types: 'dw_giving_start_fundraiser_dismiss_pressed',
      basics: 'dw_giving_create_fundraiser_dismiss_pressed',
      story: 'dw_giving_fundraiser_tell_story_dismiss_pressed',
      cover: 'dw_giving_fundraiser_add_photo_dismiss_pressed',
      nonprofit: 'dw_giving_find_fundraiser_charity_dismiss_pressed',
    }
    if (stepToEventNameMapping[currentStep]) {
      trackEventNameClick({
        event_name: stepToEventNameMapping[currentStep],
        dismiss_method: 'close',
        ...this.getFPTITags(),
      })
    } else {
      this.trackFPTI('click', 'pools_create_error_close_pressed', 'close')
    }
  }

  handleClose = () => {
    const currentStep =
      this.props.campaignType === CROWD_FUNDING
        ? getStepName('create-crowdfunding', this.props.routes)
        : getStepName('create', this.props.routes)
    this.handleDismissEvents(currentStep)
    if (
      currentStep === 'types' ||
      currentStep === 'publish' ||
      currentStep === 'share/:campaignId'
    ) {
      this.handleCloseDialogOk()
    } else {
      this.setState({ showCloseDialog: true })
      trackImpression({
        event_name: 'dw_giving_quit_creating_fundraiser_screen_shown',
        ...this.getFPTITags(),
      })
    }
  }

  closeDialog = () => {
    this.setState({ showCloseDialog: false })
    trackEventNameClick({
      event_name: 'dw_giving_quit_creating_fundraiser_cancel_pressed',
      ...this.getFPTITags(),
    })
  }

  handleCloseDialogOk = () => {
    trackEventNameClick({
      event_name: 'dw_giving_quit_creating_fundraiser_quit_pressed',
      ...this.getFPTITags(),
    })
    if (
      document.referrer.indexOf('/create') > -1 ||
      document.referrer.indexOf('/create-crowdfunding') > -1
    ) {
      // sometimes the referrer is the same page
      location.href = hubPageUrl()
    } else {
      location.href = document.referrer || hubPageUrl()
    }
  }

  getErrorComponent = errorType => {
    const { charity } = this.props
    const shouldShowButton =
      errorType === CREATION_BLOCKS.byAccountType ||
      errorType === CREATION_BLOCKS.byUnconfirmed
    trackImpression({
      error_desc: errorType || 'CREATE_ERROR',
      error_code: 500,
      flow: charity ? 'GENEROSITY_NETWORK_CHARITIES' : 'GENEROSITY_NETWORK',
      event_name: 'dw_giving_failed_customer_interaction_occurred',
      screen: 'start_fundraiser',
      event_type: 'ac',
    })

    return (
      <Modal onClose={this.handleClose}>
        {this.props.blockType === CREATION_BLOCKS.byGNAccountRestricted ? (
          <StandardError
            iconType="warning"
            title={i18n('error_account_restricted_title')}
            subtitle={i18n('error_account_restricted_sub_title')}
            extraContent={
              <Button
                as="a"
                href={`${GN_DISCLAIMERS[this.props.countryCode].userAgreement}`}
                role="button"
                secondary
              >
                {i18n('error_account_restricted_button')}
              </Button>
            }
          />
        ) : (
          <StandardError
            iconType="warning"
            title={i18n(`${errorType}_title`)}
            subtitle={i18n(`${errorType}_subtitle`, {
              country: this.props.countryName,
              email: this.props.email,
            })}
            showButton={shouldShowButton}
            backButton={shouldShowButton}
            extraContent={
              errorType === CREATION_BLOCKS.byUnconfirmed ? (
                <a styleName="settings-link" href="/myaccount/settings">
                  {i18n('unconfirmed_settings_link')}
                </a>
              ) : (
                undefined
              )
            }
          />
        )}
      </Modal>
    )
  }

  render() {
    const child = React.Children.only(this.props.children)
    const { cdnHost } = this.props
    let stepName = this.props.params.nextStep
    let prevStepName = this.props.params.prevStep
    const isInitialState =
      this.props.location.state && this.props.location.state.initial

    let key
    let WrappingComponent
    if (stepName === 'preview') {
      WrappingComponent = PreviewHeader
      key = 'preview'
    } else {
      WrappingComponent = ModalWithAnimations
      key = 'wizard'
    }

    const movingForward = this.props.location.action !== 'POP'
    let transitionNames
    let animateModal
    if (prevStepName === 'preview') {
      animateModal = false
      transitionNames = {
        enter: styles['create-modal-enter'],
        enterActive: styles['create-modal-enter-active'],
        leave: styles['preview-leave'],
        leaveActive: styles['preview-leave-active'],
      }
    } else if (stepName === 'preview') {
      animateModal = false
      transitionNames = {
        enter: styles['preview-enter'],
        enterActive: styles['preview-enter-active'],
        leave: styles['create-modal-leave'],
        leaveActive: styles['create-modal-leave-active'],
      }
    } else {
      animateModal = true
      transitionNames = {}
    }

    if (this.props.blockType) {
      return this.getErrorComponent(this.props.blockType)
    }

    const COVER = `https://${cdnHost}/cause-gn-images/create-cover.svg`
    const moneypoolStep = getStepName('create', this.props.routes)
    const crowdfundingStep = getStepName(
      'create-crowdfunding',
      this.props.routes
    )

    let headerImageSrc = ''
    let showHeader = this.props.campaignType === CROWD_FUNDING

    if (moneypoolStep === 'types') {
      showHeader = true
      headerImageSrc = COVER
    }

    switch (crowdfundingStep) {
      case 'basics':
        headerImageSrc = COVER
        break
      case 'nonprofit':
        headerImageSrc = COVER
        break
      case 'story':
        headerImageSrc = `https://${cdnHost}/cause-gn-images/create-header-story.svg`
        break
      case 'cover':
        headerImageSrc = `https://${cdnHost}/cause-gn-images/create-header-cover.svg`
        break
      default:
        break
    }
    return (
      <div>
        {this.state.showCloseDialog && (
          <StandardDialog
            size="medium"
            title={i18n('close_dialog_title')}
            subtitle={i18n('close_dialog_subtitle')}
            closeButtonTitle={
              this.props.campaignType === CROWD_FUNDING
                ? i18n('keep_creating_cf_button')
                : i18n('keep_creating_button')
            }
            okButtonTitle={
              this.props.campaignType === CROWD_FUNDING
                ? i18n('quit_cf_button')
                : i18n('quit_button')
            }
            onClose={this.closeDialog}
            onOk={this.handleCloseDialogOk}
          />
        )}
        <ReactCSSTransitionGroup
          transitionName={transitionNames}
          transitionEnterTimeout={TRANSITION_DURATION}
          transitionLeaveTimeout={TRANSITION_DURATION}
          component="div"
          styleName="preview-animation-container"
        >
          <WrappingComponent
            key={key}
            movingForward={movingForward}
            hideBack={
              (stepName === 'publish' && !this.props.creationError) ||
              isInitialState
            }
            hideClose={this.props.hideClose}
            onClose={this.handleClose}
            animate={animateModal}
            showHeader={showHeader}
            headerImageSrc={headerImageSrc}
            isWideView={this.props.campaignType === CROWD_FUNDING}
          >
            {React.cloneElement(child, { key: stepName })}
          </WrappingComponent>
        </ReactCSSTransitionGroup>
      </div>
    )
  }
}

export default cssModules(CreatePage, styles)
